import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiDatabase,
  FiDownload,
  FiFileText,
  FiRefreshCw,
  FiShield,
  FiUpload,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "No backup yet";
}

export default function ToolsDashboard() {
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();
  const [operations, setOperations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const production = process.env.NODE_ENV === "production";
  const canViewOperations = !production && hasPermission(user, "tools.system_jobs.view");
  const loadOperations = useCallback(async () => {
    if (!accessToken || !canViewOperations) return;
    setIsLoading(true);
    try {
      setOperations(await apiRequest("/system-operations", { accessToken }));
    } catch {
      setOperations(null);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, canViewOperations]);
  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  const tools = useMemo(
    () =>
      [
        {
          title: "Import History",
          description: "Review confirmed Excel imports and validation results.",
          icon: FiFileText,
          color: "bg-amber-50 text-amber-700",
          href: "/admindashboard/tools/importhistory",
          visible: hasPermission(user, "imports.view"),
        },
        {
          title: "Audit Logs",
          description: "Review recorded ERP actions and their actors.",
          icon: FiShield,
          color: "bg-red-50 text-red-700",
          href: "/admindashboard/tools/auditlogs",
          visible: hasPermission(user, "audit_logs.view"),
        },
        {
          title: "Backup & Restore",
          description:
            "Create, download, and manage protected database backups.",
          icon: FiDatabase,
          color: "bg-indigo-50 text-indigo-700",
          href: "/admindashboard/tools/backuprestore",
          visible: !production && hasPermission(user, "tools.backup_restore.view"),
        },
        {
          title: "System Jobs",
          description: "Monitor live backup, import, and biometric activity.",
          icon: FiActivity,
          color: "bg-cyan-50 text-cyan-700",
          href: "/admindashboard/tools/systemjobs",
          visible: canViewOperations,
        },
      ].filter((tool) => tool.visible),
    [canViewOperations, production, user],
  );

  const stats = [
    {
      label: "Imports today",
      value: operations?.summary?.importsToday ?? "—",
      accent: "text-primary-800",
    },
    {
      label: "Audit events today",
      value: operations?.summary?.auditEventsToday ?? "—",
      accent: "text-violet-700",
    },
    {
      label: "Stored backups",
      value: operations?.summary?.backups?.total ?? "—",
      accent: "text-green-700",
    },
    {
      label: "Last backup",
      value: operations?.summary?.backups?.latestBackupAt
        ? formatDate(operations.summary.backups.latestBackupAt)
        : "—",
      accent: "text-amber-700",
      compact: true,
    },
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-white to-primary-50/60 p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-700">
              Administration
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-950">
              Tools center
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600">
              Your authorized workspace for import history and audit records.
            </p>
          </div>
          {canViewOperations ? (
            <button
              type="button"
              onClick={loadOperations}
              disabled={isLoading}
              className="inline-flex h-11 items-center gap-2 self-start rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:border-primary-200 hover:bg-primary-50 disabled:opacity-60"
            >
              <FiRefreshCw className={isLoading ? "animate-spin" : ""} />{" "}
              Refresh
            </button>
          ) : null}
        </div>
      </section>

      {canViewOperations ? (
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p
                className={`mt-2 font-bold ${stat.compact ? "text-sm" : "text-2xl"} ${stat.accent}`}
              >
                {stat.value}
              </p>
            </div>
          ))}
        </section>
      ) : !production ? (
        <section className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600">
          Operational statistics are available to users with{" "}
          <code>tools.system_jobs.view</code>.
        </section>
      ) : null}

      <section>
        <div className="mb-4">
          <h2 className="text-lg font-bold text-gray-950">Available tools</h2>
          <p className="mt-1 text-sm text-gray-500">
            Only pages allowed by your current permissions are shown.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                type="button"
                key={tool.title}
                onClick={() => navigate(tool.href)}
                className="group rounded-3xl border border-gray-200 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg"
              >
                <span
                  className={`grid h-12 w-12 place-items-center rounded-2xl ${tool.color}`}
                >
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 text-lg font-bold text-gray-950">
                  {tool.title}
                </h3>
                <p className="mt-2 min-h-10 text-sm leading-5 text-gray-600">
                  {tool.description}
                </p>
                <span className="mt-5 inline-block text-sm font-bold text-primary-800 group-hover:underline">
                  Open →
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
